import { useMemo, useState } from 'react';

export type SortDirection = 'asc' | 'desc';
export type SortState = {
  key: string | null;
  direction: SortDirection;
};

const getNestedValue = (obj: any, key: string): any => {
  return key.split('.').reduce((current: any, prop) => current?.[prop], obj);
};

export function useSearchFilter<T>(
  data: T[],
  searchableKeys: string[],
  initialSortKey: string | null = null,
  initialSortDirection: SortDirection = 'asc'
) {
  const [searchQuery, setSearchQuery] = useState('');
  const [sortState, setSortState] = useState<SortState>({
    key: initialSortKey,
    direction: initialSortDirection,
  });

  const processedData = useMemo(() => {
    let result = data;
    const lowerCaseQuery = searchQuery.toLowerCase();

    if (searchQuery) {
      result = data.filter((item) => {
        return searchableKeys.some((key) => {
          const value = getNestedValue(item, key);
          return String(value || '')
            .toLowerCase()
            .includes(lowerCaseQuery);
        });
      });
    }

    const { key, direction } = sortState;

    if (key) {
      result.sort((a, b) => {
        const aValue = getNestedValue(a, key);
        const bValue = getNestedValue(b, key);

        if (aValue == null && bValue == null) return 0;
        if (aValue == null) return direction === 'asc' ? 1 : -1;
        if (bValue == null) return direction === 'asc' ? -1 : 1;

        let comparison = 0;

        const aNum = Number(aValue);
        const bNum = Number(bValue);

        if (!isNaN(aNum) && !isNaN(bNum)) {
          comparison = aNum - bNum;
        } else {
          const valA = String(aValue).toLowerCase();
          const valB = String(bValue).toLowerCase();

          if (valA < valB) comparison = -1;
          else if (valA > valB) comparison = 1;
        }
        return direction === 'asc' ? comparison : -comparison;
      });
    }

    return result;
  }, [data, searchQuery, searchableKeys, sortState]);

  const handleSort = (newKey: string) => {
    setSortState((prevState) => {
      if (prevState.key === newKey) {
        return {
          key: newKey,
          direction: prevState.direction === 'asc' ? 'desc' : 'asc',
        };
      } else {
        return {
          key: newKey,
          direction: 'asc',
        };
      }
    });
  };

  return {
    filteredData: processedData, // Alias changed back to filteredData
    searchQuery,
    setSearchQuery,
    sortState,
    handleSort,
  };
}
